import type { AddressResult } from '@alexanderathoodly/data-models'

interface LocalBusiness {
	'@context': 'https://schema.org'
	'@type': 'LocalBusiness'
	address: {
		'@type': 'PostalAddress'
		addressLocality: string
		addressRegion: string
		streetAddress: string
	}
	description: string
	name: string
	image?: string
	priceRange: string
	additionalType: 'Coworking office'
	url: string
	brand: string
	keywords: string
	tourBookingPage: string
	review?: Review[]
	amenityFeature?: AmenityFeature[]
	offers?: Offer[]
}

export interface AmenityFeature {
	'@type': 'LocationFeatureSpecification'
	name: string
	value: boolean
}

export interface Review {
	'@type': 'Review'
	reviewBody: string
	author: { '@type': 'Person'; name: string }
}

export interface Offer {
	'@type': 'Offer'
	'@id': string
	url: string
	businessFunction: 'http://purl.org/goodrelations/v1#LeaseOut'
	availability: 'https://schema.org/InStock'
	category: 'Commercial > Office space'
	priceCurrency: 'SEK'
	price?: string
	priceSpecification?: UnitPriceSpecification
	itemOffered: ItemOffered
}

export interface ItemOffered {
	'@type': 'Membership' | 'Room' | 'Suite' //Big Office and Flex = Suite
	name: string
	identifier: string
	image?: string
	maximumAttendeeCapacity?: string
	floorSize?: FloorSize
	additionalType: 'https://en.wikipedia.org/wiki/Serviced_office'
}

export interface UnitPriceSpecification {
	'@type': 'UnitPriceSpecification'
	priceCurrency: 'SEK'
	price: string
	unitCode: 'MON'
}

export interface FloorSize {
	'@type': 'QuantitativeValue'
	value: string
	unitCode: 'MTK'
}

export interface OfferInfo {
	type: 'membership' | 'rooms' | 'bigOffice' | 'flexOffice'
	name: string
	identifier: string
	price?: number | undefined
	size?: number | undefined
	workspaces?: number | undefined
	image?: string | undefined
}

export function getLocalBusinessObj(
	name: string,
	brand: string,
	description: string,
	address: AddressResult,
	priceRange: string,
	url: string,
	keywords: string,
	image?: string | undefined,
	amenities?: string[],
	reviews?: { review: string; author: string }[],
	offersInfo?: OfferInfo[]
): LocalBusiness {
	const localBusiness: LocalBusiness = {
		'@context': 'https://schema.org',
		'@type': 'LocalBusiness',
		address: {
			'@type': 'PostalAddress',
			addressLocality: address.locator,
			addressRegion: address.city,
			streetAddress: address.street,
		},
		description,
		name: name,
		priceRange: `Från ${priceRange}kr/person/månad`,
		additionalType: 'Coworking office',
		url: `https://yta.se/kontorshotell/${url}`,
		brand,
		keywords,
		tourBookingPage: `https://yta.se/kontorshotell/${url}`,
	}
	if (image) {
		localBusiness.image = image
	}
	if (reviews) {
		localBusiness.review = reviews.map((r) => {
			return {
				'@type': 'Review',
				reviewBody: r.review,
				author: { '@type': 'Person', name: r.author },
			}
		})
	}
	if (amenities) {
		localBusiness.amenityFeature = amenities.map((a) => {
			return {
				'@type': 'LocationFeatureSpecification',
				name: a,
				value: true,
			}
		})
	}
	if (offersInfo && offersInfo.length > 0) {
		localBusiness.offers = getOfferFromOfferInfo(offersInfo, url)
	}
	return localBusiness
}

function getOfferFromOfferInfo(
	offersInfo: OfferInfo[],
	locationUrl: string
): Offer[] {
	return offersInfo.map((offerInfo) => {
		const offer: Offer = {
			'@type': 'Offer',
			'@id': `https://yta.se/kontorshotell/${locationUrl}-${offerInfo.identifier}`,
			url: `https://yta.se/kontorshotell/${locationUrl}-${offerInfo.identifier}`,
			businessFunction: 'http://purl.org/goodrelations/v1#LeaseOut',
			availability: 'https://schema.org/InStock',
			category: 'Commercial > Office space',
			priceCurrency: 'SEK',
			itemOffered: {
				'@type':
					offerInfo.type == 'membership'
						? 'Membership'
						: offerInfo.type == 'bigOffice' || offerInfo.type == 'flexOffice'
						? 'Suite'
						: 'Room',
				name: offerInfo.name,
				identifier: offerInfo.identifier,
				additionalType: 'https://en.wikipedia.org/wiki/Serviced_office',
			},
		}
		if (offerInfo.price) {
			offer.price = offerInfo.price.toString()
			offer.priceSpecification = {
				'@type': 'UnitPriceSpecification',
				priceCurrency: 'SEK',
				price: offerInfo.price.toString(),
				unitCode: 'MON',
			}
		}
		if (offerInfo.size) {
			offer.itemOffered.floorSize = {
				'@type': 'QuantitativeValue',
				value: offerInfo.size.toString(),
				unitCode: 'MTK',
			}
		}
		if (offerInfo.workspaces) {
			offer.itemOffered.maximumAttendeeCapacity =
				offerInfo.workspaces.toString()
		}
		if (offerInfo.image) {
			offer.itemOffered.image = offerInfo.image
		}
		return offer
	})
}
